import { randomUUID } from "node:crypto";
import type {
  Attributes,
  GenerationEvent,
  Span,
  SpanOptions,
  SpanResult,
  TelemetryProvider,
  Trace,
  TraceOptions,
} from "./types.js";

const noopSpan = (traceId: string): Span => ({
  spanId: randomUUID().slice(0, 16),
  traceId,
});

/**
 * Zero-cost telemetry provider. All methods are stubs that return
 * valid objects but perform no I/O. Used when telemetry is disabled
 * (the default).
 *
 * `initTelemetry()` installs this provider when `provider: "none"` (the default)
 * or when no config is supplied. Bridge, runner, and connector packages receive
 * it via constructor injection and call its methods without any guard — every
 * call is a no-op with negligible overhead.
 *
 * @docLink packages/telemetry/concepts#noop-telemetry-provider
 */
export class NoopTelemetryProvider implements TelemetryProvider {
  startTrace(opts: TraceOptions): Trace {
    const traceId = randomUUID().replace(/-/g, "");
    return { traceId, rootSpan: noopSpan(traceId) };
  }

  endTrace(_trace: Trace, _result?: SpanResult): void {}

  startSpan(parent: Trace | Span, _opts: SpanOptions): Span {
    return noopSpan(parent.traceId);
  }

  endSpan(_span: Span, _result?: SpanResult): void {}

  addEvent(_span: Span, _name: string, _attrs?: Attributes): void {}

  logGeneration(_span: Span, _generation: GenerationEvent): void {}

  recordMetric(_name: string, _value: number, _tags?: Record<string, string>): void {}

  async flush(): Promise<void> {}

  async shutdown(): Promise<void> {}
}
